import { Content } from "@/ui";
import { getRoute } from "@/functions/getRoute";
import unslug from "unslug";
import { simple_pages_helper } from "@/helpers/simple_pages_helper";
import { jobs_helper } from "@/helpers/jobs_helper";
import { createTitle } from "@/functions/createTitle";
import { consultant_helper } from "@/helpers/consultant_helper";
import { content_tiles_helper } from "@/helpers/content_tiles_helper";
import { instagram_helper } from "@/helpers/instagram_helper";
import { benefits_helper } from "@/helpers/benefits_helper";
import { values_helper } from "@/helpers/values_helper";
import { relocations_helper } from "@/helpers/relocations_helper";
import { employee_feedback_helper } from "@/helpers/employee_feedback_helper";
import { Cookies } from "@/components";

// Page Builder Components
import { DynamicPageSeo } from "@/ui/DynamicPageSeo";
import { BuilderContentBlocks } from "@/ui/BuilderContentBlocks";
import { getDynamicPageStaticProps } from "@/functions/getDynamicPageStaticProps";
import dynamicPages from "../../.sourceflow/dynamic_pages.json";
import { SourceFlowHead } from "@sourceflow-uk/sourceflow-head";
import metaObject from "../../.sourceflow/metadata.json";

export default function Page({ content, isBuilder, seo, title, canonical, data, prefix, page, url_slugs }) {
  const stripHtmlTags = (input) => {
    if (!input) return "";
    return input.replace(/<\/?[^>]+(>|$)/g, "").trim();
  };

  return isBuilder ? (
    <>
      <DynamicPageSeo
        {...seo}
        title={seo?.title?.length > 0 ? seo.title : title}
        canonical={seo?.canonical?.length > 0 ? seo.canonical : canonical}
      />
      <BuilderContentBlocks data={data} prefix={prefix} />
    </>
  ) : (
    <>
      <SourceFlowHead
        metaObject={metaObject}
        title={page?.title || ""}
        description={stripHtmlTags(page?.description)}
        canonical={`https://www.xcede.com${getRoute("dynamic", { url_slugs })}`.replace(/\/$/, "")}
      />
      <Content items={content} />
    </>
  );
}

export async function getStaticProps({ params: { url_slugs } }) {
  const url_slug = url_slugs.join("/");

  // Prefer dynamic builder page if it exists
  const builderPage = dynamicPages.find((p) => p.url_slug === url_slug);
  if (builderPage) {
    return getDynamicPageStaticProps({ page: builderPage, url_slug });
  }

  // Fallback to simple static page
  const pages = url_slugs.map((url_slug, k) => ({
    url_slug,
    label: unslug(url_slug),
    href: getRoute("dynamic", { url_slugs: url_slugs.slice(0, k + 1) }),
  }));
  let [_page, prevPage] = [...pages].reverse();
  const page = simple_pages_helper.find(_page.url_slug);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const SPECIALISM_OVERRIDES = {
    "specialisms/automation-controls": "Latest Automation Jobs",
    "specialisms/giga-factory": "Latest Giga Factory Jobs",
    "specialisms/semiconductors": "Latest Semiconductor Jobs",
    "specialisms/data-centres": "Latest Data Centre Jobs",
    "specialisms/conventional-power": "Latest Conventional Power Jobs",
    "specialisms/transmission-distribution": "Latest Transmission & Distribution Jobs",
    "specialisms/electric-charging": "Latest Electric Charging Jobs",
    "specialisms/energy-storage": "Latest Energy Storage Jobs",
    "specialisms/offshore-wind": "Latest Offshore Wind Jobs",
    "specialisms/onshore-wind": "Latest Onshore Wind Jobs",
    "specialisms/solar": "Latest Solar Jobs",
    "specialisms/hydrogen": "Latest Hydrogen Jobs",
  };

  if (SPECIALISM_OVERRIDES[url_slug]) {
    if (page.latest_jobs_title) {
      page.latest_jobs_title = SPECIALISM_OVERRIDES[url_slug];
    }
    if (page.meet_the_team_title) {
      page.meet_the_team_title = "Meet the Team";
    }
    if (
      page.specialism_categories &&
      page.specialism_categories.length > 0 &&
      page.specialism_categories[0].specialism_title
    ) {
      page.specialism_categories[0].specialism_title = "Specialisms";
    }
  }

  const filtered_pages = pages.filter((_page) => {
    return _page.url_slug != "specialism" && _page.url_slug != "disciplines";
  });

  const all_jobs = jobs_helper.fetch();

  return {
    props: {
      url_slugs,
      meta: {
        title: createTitle(...pages.map((i) => i.label).reverse()),
      },
      content: [
        {
          component: "BreadcrumbNavigation",
          props: {
            items: filtered_pages,
            className: "mt-4",
          },
        },
        page.simple_page
          ? {
              component: "HeroSimple",
              id: "HeroSimple",
              props: {
                title: page.title || "",
                body: page.description || "",
                link: page.thank_you_page || "",
                formId: page.form_id || "",
                image: page.image || page.hero_image || "",
                thank_you_page: page.thank_you_page || "",
                newTab: page.new_tab || "",
              },
            }
          : {
              component: "Hero2",
              id: "Hero2",
              props: {
                label: page.title,
                highlight: page.highlighted_title || "",
                content: page.subtitle || "",
                body: page.description || "",
                moreBody: page.read_more_description || "",
                className: "hero-section",
                ctas: page.ctas || [],
                link: page.read_more_link || "",
                image: page.hero_image || "",
              },
            },
        page.specialism_categories && page.specialism_categories.length == 1 && page.specialism_categories[0].parent_id
          ? {
              component: "SpecialismCategories",
              id: "SpecialismCategories",
              props: {
                label: page.specialism_categories[0].specialism_title,
                parentId: page.specialism_categories[0].parent_id,
                slug: page.url_slug,
              },
            }
          : {},
        page.social_title
          ? {
              component: "Carousel",
              props: {
                title: "",
                items: instagram_helper.fetch(),
              },
            }
          : {},
        page.benefits_title
          ? {
              component: "Benefits",
              props: {
                title: page.benefits_title,
                benefits: benefits_helper.fetch(),
              },
            }
          : {},
        page.values_title
          ? {
              component: "OurValues",
              props: {
                title: page.values_title,
                brands: values_helper.fetch(),
              },
            }
          : {},
        page.employee_feedbacks
          ? {
              component: "EmployeeFeedbacks",
              props: {
                items: employee_feedback_helper.fetch(),
              },
            }
          : {},
        page.id != "ef069d6a-02e8-4208-964b-ab70e20c7730" && page.latest_jobs_title
          ? {
              component: "LatestJobs",
              id: "LatestJobs",
              props: {
                title: page.latest_jobs_title,
                jobs: all_jobs,
                variant: "tertiary",
                specialism:
                  page.specialism_categories &&
                  page.specialism_categories.length == 1 &&
                  page.specialism_categories[0].specialism_id
                    ? page.specialism_categories[0].specialism_id
                    : null,
              },
            }
          : {},
        page.meet_the_team_title
          ? {
              component: "MeetTheTeamPanel",
              props: {
                title: page.meet_the_team_title,
                members: consultant_helper.fetch({ specialty: page.specialism }),
              },
            }
          : {},
        page.ceo_statement
          ? {
              component: "CEOStatement",
              props: {
                statement: page.ceo_statement,
                content: page.ceo_content,
              },
            }
          : {},
        page.charity_statement
          ? {
              component: "CharityStatement",
              props: {
                statement: page.charity_statement,
                content: page.charity_content,
                image: page.charity_image,
              },
            }
          : {},
        page.carousel_category
          ? {
              component: "HomeTiles",
              props: {
                items: content_tiles_helper.fetch({ category: page.carousel_category }),
                style: page.carousel_style || "",
              },
            }
          : {},
        page.relocation_title
          ? {
              component: "Relocations",
              props: {
                title: page.relocation_title,
                body: page.relocation_description,
                items: relocations_helper.fetch(),
              },
            }
          : {},
        page.blog_title
          ? {
              component: "LatestBlogs",
              props: {
                title: page.blog_title,
                variant: "quaternary",
              },
            }
          : {},
        page.reviews_title
          ? {
              component: "FeefoReviews",
              props: {
                title: page.reviews_title,
              },
            }
          : {},
        page.show_ethy
          ? {
              component: "EthyWidget",
            }
          : {},
        page.id == "ef069d6a-02e8-4208-964b-ab70e20c7730" && page.latest_jobs_title
          ? {
              component: "LatestJobs",
              id: "LatestJobs",
              props: {
                title: page.latest_jobs_title,
                jobs: all_jobs,
                variant: "tertiary",
              },
            }
          : {},
        page.seo_job_description
          ? {
              component: "SeoJobsFeed",
              props: {
                description: page.seo_job_description,
                links: page.seo_links,
              },
            }
          : {},
      ],
      page,
    },
  };
}

export async function getStaticPaths() {
  // 1. Builder pages (exclude root)
  const builderPaths = dynamicPages
    .filter((i) => i.url_slug !== "/")
    .map((i) => ({
      params: { url_slugs: i.url_slug.split("/") },
    }));

  // 2. Nested simple pages (with excludes)
  const nestedSimplePaths = simple_pages_helper.toNestedPaths({
    exclude: [
      "c5fa9aeb-0606-4e67-ab43-427c0d0a23e4", // About us
      "c1bff500-061c-431c-8cab-97b45c8c57d5", // Disciplines
      "adbe3caf-9b1a-4a10-8f92-ea0b12187c3c", // Specialisms
    ],
  });

  return {
    paths: [...builderPaths, ...nestedSimplePaths],
    fallback: false,
  };
}
